import { ref } from "vue";
import { i18n } from "@/shared/i18n";

/**
 * WHOSE LOCALE A NUMBER IS WRITTEN IN — the question every formatter asks first.
 *
 * ── THE TWO CONTEXTS, WHICH ARE NOT THE SAME ──────────────────────────────
 *
 * `panelContext()` is what an OPERATOR reads: the panel language, which is their
 * own (person → workspace → browser → English), with the workspace's currency
 * and the workspace's timezone. The reader chooses how "1.234,56" is punctuated;
 * they do not get to choose what currency the invoice is in, nor what "today"
 * means for a report.
 *
 * `tenantContext()` is what a CUSTOMER reads: everything the workspace's — a
 * public form, a document, a page of the site, an invoice line. It must never
 * follow the browser of whoever happens to be looking, or a Brazilian
 * shop's customer would read a total formatted for whoever opened the tab.
 *
 * Currency and timezone come from the workspace in BOTH, and that is deliberate:
 * the panel of a company in São Paulo shows the same money and the same hours to
 * a colleague working from Lisbon, because those are facts about the business
 * and not about the reader.
 */
export interface FormatContext {
	/** BCP-47 tag — decides separators, month names and word order. */
	readonly locale: string;
	/** ISO-4217 — the workspace's, always. */
	readonly currency: string;
	/** IANA zone — the workspace's, always. What "today" means. */
	readonly timeZone: string;
	/**
	 * ISO 3166-1 alpha-2 — o país do workspace, quando já é conhecido.
	 *
	 * Não formata nada sozinho: viaja aqui porque este é o objeto que a casca já
	 * publica com os factos do workspace, e o campo de telefone precisa de saber
	 * que bandeira abrir. Um segundo canal para uma segunda propriedade do mesmo
	 * workspace seria o que fez `money.ts` existir quatro vezes.
	 */
	readonly country?: string;
}

/**
 * What the workspace says, before it has said anything.
 *
 * The same three values `App.vue` has been falling back to since the account
 * screen existed. They are wrong for a workspace outside Brazil and they are
 * only ever on screen for the milliseconds before `/v3/workspace` answers — the
 * alternative, blanking every number until then, is worse.
 */
const FALLBACK = { currency: "BRL", timeZone: "America/Sao_Paulo", locale: "pt-BR" };

// Tipado pelo contrato e não pelo formato do literal: sem a anotação, `tenant`
// nasce com o shape exato de `FALLBACK` — três campos — e o `country`, que é
// opcional e por isso não está lá, torna-se impossível de escrever depois.
const tenant = ref<FormatContext>({ ...FALLBACK });

/**
 * What the workspace is, told once by the shell.
 *
 * A module-level ref and not an injection, because the callers that most need it
 * are not components: a mutation's `onError`, a toast, a CSV column, a
 * `describeX` helper. It stays reactive — read inside a render or a `computed`,
 * it re-runs when the workspace answers or when someone changes the timezone.
 *
 * `App.vue` is the only caller. Anything else writing here would be a screen
 * deciding what currency the company bills in.
 */
export function setTenantDisplay(display: Partial<FormatContext>): void {
	tenant.value = {
		currency: display.currency || FALLBACK.currency,
		timeZone: display.timeZone || FALLBACK.timeZone,
		locale: display.locale || FALLBACK.locale,
		// Sem `||` para um fallback: um país que ainda não chegou é `undefined`,
		// que é "não sei" — e "não sei" não é o Brasil.
		country: display.country,
	};
}

/** How an OPERATOR reads it: their panel language, the workspace's money and hours. */
export function panelContext(): FormatContext {
	return {
		locale: i18n.global.locale.value,
		currency: tenant.value.currency,
		timeZone: tenant.value.timeZone,
		country: tenant.value.country,
	};
}

/** How a CUSTOMER reads it: the workspace's language, money and hours. Never the browser's. */
export function tenantContext(): FormatContext {
	return { ...tenant.value };
}
